import DesktopPlatformItem, { MobilePlatformItem } from 'components/changelog-page/PlatformItem'
import { FeatureChanger, FeatureChangerWrapper } from 'components/features-page/FeatureSelector'
import StaticPageLayout from 'components/StaticPageLayout'
import Tag from 'components/Tag'
import TitleAndSubtitle from 'components/UI/TitleAndSubtitle'
import { graphql } from 'gatsby'
import AndroidBackground from 'images/changelog/android.svg'
import ChromeBackground from 'images/changelog/chrome.svg'
import EdgeBackground from 'images/changelog/edge.svg'
import FirefoxBackground from 'images/changelog/firefox.svg'
import FireTVBackground from 'images/changelog/firetv.svg'
import IEBackground from 'images/changelog/ie.svg'
import IosBackground from 'images/changelog/ios.svg'
import LinuxBackground from 'images/changelog/linux.svg'
import MacOSBackground from 'images/changelog/macos.svg'
import TiVoBackground from 'images/changelog/tivo.svg'
import WindowsBackground from 'images/changelog/windows.svg'
import { ReactElement, ReactNode, useEffect, useState } from 'react'
import { PlatformInfo, wsApi } from 'redux/api/wsApi'
import { Flex, Grid } from 'theme-ui'
import { defineOS } from 'utils/downloadPageUtils'
import getEnglishStrings from 'utils/getEnglishStrings'
import { platforms } from 'utils/platformList'
import useCurrentPlatform from 'utils/useCurrentPlatform'
import useHasMounted from 'utils/useHasMounted'

interface SelectedPlatform {
  title: string
  icon: string
  platforms: PlatformInfo[]
}

export const platformName = {
  windows: '64-bit (AMD64)',
  windows_arm64: '64-bit (ARM64)',
  mac: 'Universal',
  linux_deb_x64: 'GUI Debian/Ubuntu (AMD64)',
  linux_deb_arm64: 'GUI Debian/Ubuntu (ARM64)',
  linux_rpm_x64: 'GUI Fedora (AMD64)',
  linux_rpm_opensuse_x64: 'GUI openSUSE (AMD64)',
  linux_zst_x64: 'GUI Arch (AMD64)',
  linux_deb_x64_cli: 'CLI Debian/Ubuntu (AMD64)',
  linux_deb_arm64_cli: 'CLI Debian/Ubuntu (ARM64)',
  linux_rpm_x64_cli: 'CLI Fedora (AMD64)',
  linux_rpm_opensuse_x64_cli: 'CLI openSUSE (AMD64)',
  linux_zst_x64_cli: 'CLI Arch (AMD64)',
}

function isValidPlatform(item: PlatformInfo | null | undefined): item is PlatformInfo {
  return item !== null && item !== undefined
}

export default function Changelog({ data }: { data: Queries.ChangelogPageQuery }): ReactElement {
  const {
    data: changelogSummaryData,
    isError,
    isFetching,
  } = wsApi.useChangelogSummaryQuery('changelogSummary', {
    refetchOnMountOrArgChange: true,
  })

  const hasMounted = useHasMounted()
  const isLoading = isFetching || !hasMounted

  const pagelogData = getEnglishStrings(data.directus?.application_changelog?.strings)
  const generalTranslations = getEnglishStrings(data.directus?.general_translations?.strings)

  const { osName } = useCurrentPlatform()
  const [selectedFeatureSet, setSelectedFeatureSet] =
    useState<'Windows' | 'Linux GUI' | 'MacOS' | 'Linux CLI'>('Windows')

  useEffect(() => {
    if (!hasMounted) {
      return
    }

    const platform = defineOS(osName)
    const featureSet = platform === 'Linux' ? 'Linux GUI' : platform || 'Windows'
    setSelectedFeatureSet(featureSet as 'Windows' | 'Linux GUI' | 'MacOS' | 'Linux CLI')
  }, [hasMounted, osName])

  const filteredPlatforms: { [key: string]: SelectedPlatform } = {
    Windows: {
      title: 'Windows',
      icon: WindowsBackground,
      platforms: [
        changelogSummaryData?.data.desktop.windows?.[0],
        changelogSummaryData?.data.desktop.windows_arm64?.[0],
      ].filter(isValidPlatform),
    },
    MacOS: {
      title: 'MacOS',
      icon: MacOSBackground,
      platforms: [changelogSummaryData?.data.desktop.osx?.[0]].filter(isValidPlatform),
    },
    'Linux GUI': {
      title: 'Linux GUI',
      icon: LinuxBackground,
      platforms: [
        changelogSummaryData?.data.desktop.linux_deb_x64?.[0],
        changelogSummaryData?.data.desktop.linux_deb_arm64?.[0],
        changelogSummaryData?.data.desktop.linux_rpm_x64?.[0],
        changelogSummaryData?.data.desktop.linux_rpm_opensuse_x64?.[0],
        changelogSummaryData?.data.desktop.linux_zst_x64?.[0],
      ].filter(isValidPlatform),
    },
    'Linux CLI': {
      title: 'Linux CLI',
      icon: LinuxBackground,
      platforms: [
        changelogSummaryData?.data.desktop.linux_deb_x64_cli?.[0],
        changelogSummaryData?.data.desktop.linux_deb_arm64_cli?.[0],
        changelogSummaryData?.data.desktop.linux_rpm_x64_cli?.[0],
        changelogSummaryData?.data.desktop.linux_rpm_opensuse_x64_cli?.[0],
        changelogSummaryData?.data.desktop.linux_zst_x64_cli?.[0],
      ].filter(isValidPlatform),
    },
  }

  const platformList = filteredPlatforms[selectedFeatureSet].platforms.map(platform => {
    return platform.platform === 'osx' ? { ...platform, platform: 'mac' } : platform
  })

  return (
    <StaticPageLayout title={`${pagelogData?.title} | Windscribe`}>
      <Flex
        sx={{
          flexDirection: 'column',
          gap: ['4.8rem', '8rem'],
          alignItems: 'center',
          width: '100%',
          maxWidth: 'maxContentWidth',
          mb: ['1.6rem', 0],
        }}
      >
        <TitleAndSubtitle title={pagelogData?.title} subtitle={pagelogData?.subtitle} />
        <Flex sx={{ flexDirection: 'column', gap: ['2.4rem', '4rem'], width: '100%' }}>
          <Tag title={generalTranslations?.desktop ?? ''} />
          <FeatureChangerWrapper>
            <FeatureChanger
              onClick={() => setSelectedFeatureSet('Windows')}
              icon={platforms['Windows'].icon}
              title={platforms['Windows'].name}
              active={selectedFeatureSet === 'Windows'}
              dataTestId="windows-feature-changer"
            />
            <FeatureChanger
              onClick={() => setSelectedFeatureSet('MacOS')}
              icon={platforms['MacOS'].icon}
              title={platforms['MacOS'].name}
              active={selectedFeatureSet === 'MacOS'}
              dataTestId="macos-feature-changer"
            />
            <FeatureChanger
              onClick={() => setSelectedFeatureSet('Linux GUI')}
              icon={platforms['Linux'].icon}
              title={generalTranslations?.linux_gui ?? ''}
              active={selectedFeatureSet === 'Linux GUI'}
              dataTestId="linux-gui-feature-changer"
            />
            <FeatureChanger
              onClick={() => setSelectedFeatureSet('Linux CLI')}
              icon={platforms['Linux'].icon}
              title={generalTranslations?.linux_cli ?? ''}
              active={selectedFeatureSet === 'Linux CLI'}
              dataTestId="linux-cli-feature-changer"
            />
          </FeatureChangerWrapper>
          <ChangelogGrid>
            {isFetching && (
              <>
                <DesktopPlatformItem
                  title={''}
                  icon={filteredPlatforms[selectedFeatureSet].icon}
                  isError={isError}
                  isLoading={true}
                  platform={changelogSummaryData?.data.desktop.windows?.[0]}
                  link={''}
                />
                <DesktopPlatformItem
                  title={''}
                  icon={filteredPlatforms[selectedFeatureSet].icon}
                  isError={isError}
                  isLoading={true}
                  platform={changelogSummaryData?.data.desktop.windows?.[0]}
                  link={''}
                />
              </>
            )}
            {!isFetching &&
              !isError &&
              platformList.map((platform, index) => (
                <DesktopPlatformItem
                  key={index}
                  title={platformName[platform?.platform] ?? ''}
                  icon={filteredPlatforms[selectedFeatureSet].icon}
                  isError={isError}
                  isLoading={isFetching}
                  platform={platform ?? {}}
                  link={platform?.platform ?? ''}
                  dataTestId={platform?.platform ?? ''}
                />
              ))}
            {isError && (
              <>
                <DesktopPlatformItem
                  title={''}
                  icon={filteredPlatforms[selectedFeatureSet].icon}
                  isError={true}
                  isLoading={false}
                  platform={{} as PlatformInfo}
                  link={''}
                  dataTestId="error-platform"
                />
                <DesktopPlatformItem
                  title={''}
                  icon={filteredPlatforms[selectedFeatureSet].icon}
                  isError={true}
                  isLoading={isFetching}
                  platform={{} as PlatformInfo}
                  link={''}
                  dataTestId="error-platform"
                />
              </>
            )}
          </ChangelogGrid>
        </Flex>

        <Flex sx={{ flexDirection: 'column', gap: ['2.4rem', '4rem'], width: '100%' }}>
          <Tag title={generalTranslations?.mobile ?? ''} />
          <ChangelogGrid>
            <MobilePlatformItem
              title="iOS"
              icon={IosBackground}
              isError={isError}
              isLoading={isLoading}
              platform={changelogSummaryData?.data.mobile.ios?.[0]}
              link="ios"
              dataTestId="ios"
            />

            <MobilePlatformItem
              title="Android"
              icon={AndroidBackground}
              isError={isError}
              isLoading={isLoading}
              platform={changelogSummaryData?.data.mobile.android?.[0]}
              link="android"
              dataTestId="android"
            />
          </ChangelogGrid>
        </Flex>

        <Flex sx={{ flexDirection: 'column', gap: ['2.4rem', '4rem'], width: '100%' }}>
          <Tag title={generalTranslations?.extension ?? ''} />
          <ChangelogGrid>
            <MobilePlatformItem
              title="Chrome"
              icon={ChromeBackground}
              isError={isError}
              isLoading={isLoading}
              platform={changelogSummaryData?.data.extension.chrome?.[0]}
              link="chrome"
              dataTestId="chrome"
            />

            <MobilePlatformItem
              title="Firefox"
              icon={FirefoxBackground}
              isError={isError}
              isLoading={isLoading}
              platform={changelogSummaryData?.data.extension.firefox?.[0]}
              link="firefox"
              dataTestId="firefox"
            />

            <MobilePlatformItem
              title="Edge"
              icon={EdgeBackground}
              isError={isError}
              isLoading={isLoading}
              platform={changelogSummaryData?.data.extension.edge?.[0]}
              link="edge"
              dataTestId="edge"
            />

            <MobilePlatformItem
              title="Internet Explorer"
              icon={IEBackground}
              isError={isError}
              isLoading={isLoading}
              platform={
                {
                  release_full_version: '2.10.8',
                  release_date: '2024-04-20',
                  beta_full_version: '2.9.7',
                  guinea_pig_full_version: '2.9.6',
                } as PlatformInfo
              }
              link="https://www.youtube.com/watch?v=xvFZjo5PgG0"
              dataTestId="ie"
            />
          </ChangelogGrid>
        </Flex>

        <Flex
          sx={{
            flexDirection: 'column',
            gap: ['2.4rem', '4rem'],
            pb: [0, '8rem'],
            width: '100%',
          }}
        >
          <Tag title={generalTranslations?.tv ?? ''} />
          <ChangelogGrid>
            <MobilePlatformItem
              title="Android TV"
              icon={AndroidBackground}
              isError={isError}
              isLoading={isLoading}
              platform={changelogSummaryData?.data.tv?.['android-tv'][0]}
              link="androidtv"
              dataTestId="androidtv"
            />

            <MobilePlatformItem
              title="Apple TV"
              icon={MacOSBackground}
              isError={isError}
              isLoading={isLoading}
              platform={changelogSummaryData?.data.tv.appletv?.[0]}
              link="appletv"
              dataTestId="appletv"
            />

            <MobilePlatformItem
              title="Fire TV"
              icon={FireTVBackground}
              isError={isError}
              isLoading={isLoading}
              platform={changelogSummaryData?.data.tv.firetv?.[0]}
              link="firetv"
              dataTestId="firetv"
            />

            <MobilePlatformItem
              title="TiVo"
              icon={TiVoBackground}
              isError={isError}
              isLoading={isLoading}
              platform={
                {
                  release_full_version: '2.10.8',
                  release_date: '2024-04-20',
                  beta_full_version: '2.9.7',
                  guinea_pig_full_version: '2.9.6',
                } as PlatformInfo
              }
              link="https://www.youtube.com/watch?v=xvFZjo5PgG0"
              dataTestId="tivo"
            />
          </ChangelogGrid>
        </Flex>
      </Flex>
    </StaticPageLayout>
  )
}

export const data = graphql`
  query ChangelogPage {
    directus {
      application_changelog {
        strings {
          languages_code {
            code
          }
          subtitle
          title
        }
      }
      general_translations {
        strings {
          languages_code {
            code
          }
          mobile
          desktop
          extension
          tv
          linux_cli
          linux_gui
        }
      }
    }
  }
`

function ChangelogGrid({ children }: { children: ReactNode }): ReactElement {
  return (
    <Grid
      columns={[1, 2]}
      sx={{
        gap: ['1.6rem', '2.4rem'],
        maxWidth: ['100%', '100%', 'maxContentWidth'],
        '& > :nth-last-of-type(1):nth-of-type(odd)': {
          gridColumn: ['auto', '1 / -1'],
          justifySelf: ['auto', 'center'],
        },
      }}
      className="changelog-grid"
    >
      {children}
    </Grid>
  )
}
